import { formatScreenName } from '@/features/billboards/utils/billboardDetailUtils';
import type { BillboardListItem } from '@/features/billboards/utils/billboardsListUtils';
import {
  buildScreenLocation,
  valueConvertor,
} from '@/features/billboards/utils/billboardsListUtils';
import { useToggleWishlistMutation } from '@/features/wishlist/api/useWishlistQueries';
import { useCan } from '@/shared/hooks/useCan';
import { getCurrencyCode } from '@/shared/lib/format/money';
import { getOptimizedImageUrl } from '@/shared/lib/media/optimizeImageUrl';
import { getAccessToken } from '@/shared/lib/storage/token';
import { useAppSelector } from '@/shared/store/hooks';
import { Heart, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type BillboardListCardProps = {
  item: BillboardListItem;
  priority?: boolean;
  /** When set to `/screens`, detail opens inside AppLayout (with sidebar). */
  detailBasePath?: '/screens' | '/billboard';
};

export function BillboardListCard({
  item,
  priority = false,
  detailBasePath = '/billboard',
}: BillboardListCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const hasSession = Boolean(getAccessToken()) || isAuthenticated;
  const canWishlist = useCan('add-to-wishlist');
  const toggleWishlist = useToggleWishlistMutation();
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(Boolean(item.isWishlisted));
  const screenId = item._id || item.id;
  const originalUrl = item.thumbnail?.url;
  const thumbnailUrl = getOptimizedImageUrl(originalUrl, {
    width: 720,
    height: 450,
    quality: 68,
  });
  const locationLabel = buildScreenLocation(item);
  const currency = getCurrencyCode();
  const screenName = formatScreenName(item.screenName);

  useEffect(() => {
    setIsWishlisted(Boolean(item.isWishlisted));
  }, [item.isWishlisted, screenId]);

  const openDetail = () => {
    if (screenId) navigate(`${detailBasePath}/${screenId}`);
  };

  const handleToggleWishlist = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!screenId || toggleWishlist.isPending) return;

    if (!hasSession) {
      navigate('/login', {
        state: { from: location.pathname || detailBasePath },
      });
      return;
    }

    setIsWishlisted((prev) => !prev);
    toggleWishlist.mutate(screenId, {
      onError: () => setIsWishlisted(Boolean(item.isWishlisted)),
    });
  };

  return (
    <article className="group flex w-full flex-col overflow-hidden rounded-[24px] border border-[#ECEAF5] bg-white text-left shadow-[0_10px_30px_rgba(60,40,120,0.05)] transition duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_rgba(40,30,110,0.12)]">
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#E6EEFB] to-[#F4F2FB]">
        <button
          type="button"
          onClick={openDetail}
          className="absolute inset-0 z-0 block h-full w-full cursor-pointer border-none bg-transparent p-0"
          aria-label={`Open ${screenName} details`}
        >
          {thumbnailUrl && !imageFailed ? (
            <>
              {!imageLoaded ? (
                <div className="absolute inset-0 animate-pulse bg-[#EEF1F4]" />
              ) : null}
              <img
                src={thumbnailUrl}
                alt={screenName}
                loading={priority ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={priority ? 'high' : 'auto'}
                sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageFailed(true)}
                className={`h-full w-full object-cover transition duration-500 group-hover:scale-[1.03] ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </>
          ) : (
            <div className="relative flex h-full items-center justify-center overflow-hidden bg-[#EEF1F5]">
              <div
                className="relative z-[1] flex w-[78%] aspect-[16/10] items-center justify-center rounded-[11px] border border-black/[0.08] bg-[#0B1220] shadow-[0_12px_28px_-14px_rgba(15,23,42,0.45)]"
                aria-hidden="true"
              >
                <div className="absolute inset-[3px] flex items-center justify-center overflow-hidden rounded-[8px] bg-gradient-to-b from-[#151D2E] to-[#0A0F1A]">
                  <img
                    src="/favicon.svg"
                    alt=""
                    className="h-[46%] w-auto max-w-[58%] object-contain"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
        </button>

        
        <span className="pointer-events-none absolute bottom-2.5 left-2.5 z-10 rounded-full bg-white/92 px-2.5 py-1 text-xs font-extrabold text-[#3A3660] shadow-sm backdrop-blur">
          {currency} {item.basePrice ?? 0}/sec
        </span>

     
        {canWishlist ? (
          <button
            type="button"
            onClick={handleToggleWishlist}
            disabled={toggleWishlist.isPending}
            aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
            className="absolute top-2.5 right-2.5 z-10 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/92 text-[#6A6688] shadow-sm backdrop-blur transition hover:scale-105 disabled:opacity-60"
          >
            <Heart
              className={`h-4 w-4 transition-colors ${
                isWishlisted ? 'fill-[#FF5A6A] text-[#FF5A6A]' : 'text-[#6A6688]'
              }`}
              strokeWidth={2}
            />
          </button>
        ) : null}
      </div>

     
      <button
        type="button"
        onClick={openDetail}
        className="flex flex-1 cursor-pointer flex-col gap-3 border-none bg-transparent p-3.5 text-left"
      >
        <div className="min-w-0">
          <h3 className="m-0 truncate text-[15px] font-extrabold tracking-tight text-[#3A3660]">
            {screenName}
          </h3>
          <p className="mt-1 mb-0 flex items-center gap-1.5 truncate text-xs text-[#6A6688]">
            <MapPin size={12} className="shrink-0 text-[#8156B4]" />
            <span className="truncate">{locationLabel || 'Location unavailable'}</span>
          </p>
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <div className="grid grid-cols-3 gap-1.5 rounded-[14px] bg-[#F3F2F9] px-2 py-2 text-center">
            <Metric
              label="Footfall"
              value={valueConvertor(item.footfallCount ?? item.footfall)}
              color="#3B66B8"
            />
            <Metric
              label="Views"
              value={valueConvertor(item.totalViews)}
              color="#12785A"
            />
            <Metric
              label={`${currency === 'AED' ? 'AED' : '₹'}/sec`}
              value={item.basePrice != null ? String(item.basePrice) : '0'}
              color="#A15C00"
            />
          </div>

         
          <div className="grid grid-cols-2 gap-1.5 text-center text-[11px] font-bold">
            <span className="rounded-[10px] bg-[#F2E3F6] px-1 py-1.5 text-[#8A3E9E]">
              {valueConvertor(item.femaleViews)} female
            </span>
            <span className="rounded-[10px] bg-[#DFE9FA] px-1 py-1.5 text-[#3B66B8]">
              {valueConvertor(item.maleViews)} male
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

function Metric({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="min-w-0 text-center">
      <p className="m-0 text-[10px] font-semibold text-[#6A6688]">
        {label}
      </p>
      <p
        className="mt-0.5 mb-0 truncate text-sm font-extrabold"
        style={{ color }}
      >
        {value}
      </p>
    </div>
  );
}
